import { useState } from "react";
import {
  Box,
  Button,
  styled,
  Stack,
  Typography,
  Divider,
  Skeleton,
} from "@mui/material";
import KpiCard from "./kpi-card";
import { CustomButton } from "@/components/custom/custom-button";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { formatCurrency } from "@/utils/format-currency";
import { usePayoutStats } from "@/hooks/use-payout-stats";

const periods = ["7d", "14d", "30d", "60d", "90d"];

const Container = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2),
}));

const PeriodButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isActive",
})(({ theme, isActive }) => ({
  minWidth: 48,
  height: 32,
  borderRadius: 20,
  fontSize: "0.8rem",
  textTransform: "none",
  fontWeight: isActive ? 600 : 400,
  backgroundColor: isActive ? theme.palette.primary.main : "transparent",
  color: isActive ? "#fff" : theme.palette.text.secondary,
  "&:hover": {
    backgroundColor: isActive
      ? theme.palette.primary.main
      : theme.palette.action.hover,
  },
}));

const PeriodSelector = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : theme.palette.grey[100],
  borderRadius: 20,
  padding: 4,
  width: "fit-content",
  gap: 2,
}));

const directionFromPercent = (pct) =>
  pct == null ? null : pct >= 0 ? "up" : "down";

// API returns current/previous totals only — derive the change ourselves.
const pctChange = (current, previous) => {
  if (current == null || previous == null || previous === 0) return null;
  return Number((((current - previous) / previous) * 100).toFixed(1));
};

const KpiStats = () => {
  const [period, setPeriod] = useState("30d");

  // Collection & payout summary from /stats/summary, filtered by period.
  const { data: stats, isLoading } = usePayoutStats(period);

  const collections = stats?.collections;
  const payout = stats?.payout;

  const collectionAmountChange = pctChange(
    collections?.current.totalAmount,
    collections?.previous.totalAmount,
  );
  const payoutAmountChange = pctChange(
    payout?.current.totalAmount,
    payout?.previous.totalAmount,
  );
  const collectionCountChange = pctChange(
    collections?.current.totalCount,
    collections?.previous.totalCount,
  );
  const payoutCountChange = pctChange(
    payout?.current.totalCount,
    payout?.previous.totalCount,
  );

  const cards = [
    {
      id: "total_collection",
      label: "Total Collection",
      loading: isLoading,
      value: collections
        ? formatCurrency(collections.current.totalAmount)
        : "—",
      badgeType: "percent_change",
      changePercent: collectionAmountChange,
      changeDirection: directionFromPercent(collectionAmountChange),
      changeLabel: "vs previous period",
    },
    {
      id: "total_payouts",
      label: "Total Payouts",
      loading: isLoading,
      value: payout ? formatCurrency(payout.current.totalAmount) : "—",
      badgeType: "percent_change",
      changePercent: payoutAmountChange,
      changeDirection: directionFromPercent(payoutAmountChange),
      changeLabel: "vs previous period",
    },
    {
      id: "total_collection_count",
      label: "Total Collection Count",
      loading: isLoading,
      value: collections
        ? collections.current.totalCount.toLocaleString()
        : "—",
      badgeType: "percent_change",
      changePercent: collectionCountChange,
      changeDirection: directionFromPercent(collectionCountChange),
      changeLabel: "vs previous period",
    },
    {
      id: "total_payout_count",
      label: "Total Payout Count",
      loading: isLoading,
      value: payout ? payout.current.totalCount.toLocaleString() : "—",
      badgeType: "percent_change",
      changePercent: payoutCountChange,
      changeDirection: directionFromPercent(payoutCountChange),
      changeLabel: "vs previous period",
    },
    // {
    //   id: "total_customers",
    //   label: "Total customers",
    //   loading: isCustomersLoading,
    //   value: corporateCustomers?.count_formatted ?? "—",
    //   badgeType: "static_label",
    //   changeLabel: "Live total",
    // },
    // {
    //   id: "active_accounts",
    //   label: "Active accounts",
    //   loading: false,
    //   value: "23",
    //   badgeType: "static_label",
    //   changeLabel: "Live total",
    // },
  ];

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Platform summary
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Period
            </Typography>

            <PeriodSelector>
              {periods.map((p) => (
                <PeriodButton
                  key={p}
                  isActive={period === p}
                  onClick={() => setPeriod(p)}
                >
                  {p}
                </PeriodButton>
              ))}
            </PeriodSelector>
          </Box>

          <Divider orientation="vertical" flexItem />

          <CustomButton
            variantType="outline"
            endIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            onClick={() => console.log("export")}
            sx={{ height: 35 }}
          >
            Export
          </CustomButton>
        </Box>
      </Box>

      <Stack
        direction="row"
        spacing={2}
        flexWrap="wrap"
        useFlexGap
        sx={{ width: "100%" }}
      >
        {cards.map((card) => (
          <KpiCard
            key={card.id}
            label={card.label}
            value={
              card.loading ? <Skeleton width={110} height={38} /> : card.value
            }
            badgeType={card.badgeType}
            changePercent={card.changePercent}
            changeDirection={card.changeDirection}
            changeLabel={card.changeLabel}
          />
        ))}
      </Stack>
    </Container>
  );
};

export default KpiStats;
